import * as Sentry from '@sentry/react';

import React, { useEffect } from 'react';
import { Router } from 'components/router';
import { useExternalLogin } from 'hooks/use-external-login.hook';
import { Spinner } from '@hqo/react-components-library/dist/atoms/spinner';
import { defaultTheme } from '@hqo/react-components-library/dist/molecules/theme';
import { Container } from 'hocs/shared-styles';
import { useViewParams } from 'hooks/use-view-params.hook';
import { usePendo } from 'hooks/use-pendo.hook';
import { useMiniappSdkEventHandler } from 'hooks/use-miniapp-sdk.hook';
import { useNativeHeaderVisibility } from 'hooks/use-native-header-visibility.hook';
import { useDispatch, useSelector } from 'react-redux';
import { getBuilding } from 'store/building/actions';
import { Building } from 'store/building/types';
import { InitHandlerPayload } from '@hqo/hqo-miniapp-client-sdk';
import { getBuildingTheme } from 'store/theme/actions';
import { BuildingTheme } from 'store/theme/types';
import { externalLogin, getCurrentUser } from 'store/user/actions';
import { User } from 'store/user/types';
import { USER_AGENT } from 'shared/consts';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { setFeatureFlags } from 'store/featureFlags/actions';
import { updateInitialRoute } from 'store/routes/actions';
import { setAvailableCmds } from 'store/sdk/actions';
import { useInitialRoute } from 'hooks/use-initial-route.hook';
import { selectInitialRoute } from 'store/routes/selectors';
import { getPathnameFromURL } from 'utils/getPathnameFromURL';

export const App: React.FC = (): JSX.Element => {
  const shouldWaitForLogin = useExternalLogin();
  const dispatch = useDispatch();
  const defaultRoute = useInitialRoute();
  const initialRoute = useSelector(selectInitialRoute);
  useViewParams();
  usePendo();
  useNativeHeaderVisibility();
  const isDesktopApp =
    navigator.userAgent.toLowerCase().includes(USER_AGENT.CHROME) ||
    navigator.userAgent.toLowerCase().includes(USER_AGENT.SAFARI);
  const featureFlags = useFlags();

  useEffect(() => {
    dispatch(setFeatureFlags(featureFlags));
  }, [featureFlags]);

  useMiniappSdkEventHandler(
    'initResponse',
    (data: InitHandlerPayload) => {
      if (!initialRoute) {
        dispatch(updateInitialRoute(getPathnameFromURL(data?.initial_route) || defaultRoute));
      }
      // only for test debug - will be removed later
      Sentry.captureMessage('[SDK] initResponse received', {
        level: 'info',
        extra: {
          available_cmds: data?.available_cmds ?? null,
          initial_route: data?.initial_route ?? null,
          building: data?.building ?? null,
          base_url: data?.base_url ?? null,
        },
      });
      if (data?.available_cmds) {
        dispatch(setAvailableCmds(data.available_cmds));
      }
      if (isDesktopApp) {
        if (data?.jwt_token && data?.user) {
          dispatch(
            externalLogin.success({
              token: `Bearer ${data.jwt_token}`,
              user: data.user as unknown as User,
              apiUrl: null,
            }),
          );
        }
        if (data?.user) {
          dispatch(getCurrentUser.success({ user: data.user as unknown as User }));
        }
        if (data?.building) {
          dispatch(getBuilding.success(data.building as unknown as Building));
        }
        if (data?.theme) {
          dispatch(getBuildingTheme.success(data.theme as unknown as BuildingTheme));
        }
      }
    },
    [dispatch, initialRoute, defaultRoute],
  );

  if (shouldWaitForLogin) {
    return (
      <Container isLoading>
        <Spinner size="10em" color={defaultTheme.colors.$greyLight} />
      </Container>
    );
  }

  return <Router />;
};
